/**
 * The P2 telemetry groups' client side: visits and `tab.start` (`visits` group, /api/me
 * `telemetryVisitsEnabled`) and attention spans (`attention` group,
 * `telemetryAttentionEnabled`). Both stay off while their flag is missing or false.
 *
 * - services/telemetry.js `enable(...)` applies the groups and the identity from each
 *   /api/me answer (`apply`). Visits stamp `visit_id` into the ambient context of both
 *   rings (lib/logRingBuffer.js `setUiContext`); a visit change first ends the attention
 *   spans under the old id.
 * - One set of passive activity listeners feeds the attention tracker's presence clock and
 *   the visits' `last_activity_at`; they only read a clock and compare it.
 * - `tab.start` is recorded once per page load, after the first visit is stamped and the
 *   app has registered its state (hooks/useTelemetryAppState.js). Its last snapshot is
 *   attached to bug reports (lib/telemetry/bugReport.js).
 * - The entry source of the page (`?src=alert`, a copy link, a forecast link) is read once
 *   at boot (`captureTelemetryEntry`, main.jsx) and stripped from the URL by lib/view.
 *
 * Tests replace the clocks and the focus / visibility seam with
 * `window.__soothTelemetrySeam = {now, wallNow, visible, focused}` before the app loads;
 * the page then exposes `window.__soothTelemetryP2 = {checkpoint, notify, visitId, view}`.
 */
import { ringBuffer, setUiContext } from "../lib/logRingBuffer";
import { CLIENT_BUILD_SHA } from "../lib/telemetry/buildSha.js";
import { entrySourceOf } from "../lib/telemetry/entrySource.js";
import { intentEmitter } from "../lib/telemetry/intentEmitter.js";
import { recordP2Event } from "../lib/telemetry/groups.js";
import { REGISTRY_VERSION } from "../lib/telemetry/registryVersion.js";
import { buildTabStart } from "../lib/telemetry/tabStart.js";
import { routeTemplateOf } from "../lib/view/routeTemplate";
import { consumeEntrySource, readViewFromLocation } from "../lib/view/urlSync";
import { createAttentionTracker } from "./attentionTracker.js";
import { createVisits } from "./visits";

export const SEAM_GLOBAL = "__soothTelemetrySeam";
export const TEST_HOOK_GLOBAL = "__soothTelemetryP2";

/**
 * Activity: input the user made. Each handler only reads the clock (and the target, which may
 * move attention). Not activity: `pointerover` (the browser also fires it when the page moves
 * under a still pointer) and `focusin` (the app focuses fields by itself) — both only move
 * attention — and `scroll` (the app scrolls by itself while streaming; the user's own scrolling arrives as
 * wheel, keys, touch or a pointer drag) — scroll only re-measures surfaces.
 */
const TARGET_EVENTS = ["pointerdown", "touchstart"];
const PLAIN_EVENTS = ["wheel", "keydown"];

let entry = { route: null, source: "direct" };

/**
 * Read the page's entry route and source once, before anything rewrites the URL, and strip
 * `?src=` (lib/view). Called from main.jsx before route capture is installed.
 */
export function captureTelemetryEntry(win = typeof window !== "undefined" ? window : null) {
  if (!win) return entry;
  try {
    const declared = consumeEntrySource(win);
    entry = {
      route: routeTemplateOf(win.location.href),
      source: entrySourceOf({
        pathname: win.location.pathname,
        declared,
        forecastParam: readViewFromLocation(win).activeId,
      }),
    };
  } catch {
    /* direct */
  }
  return entry;
}

/** The page's entry source (`direct`, `alert`, `copy_link`, `link`): `result_seen`'s `via`. */
export function telemetryEntrySource() {
  return entry.source;
}

function safeLocalStorage() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** Create the P2 controller; the app uses the singleton below. */
export function createP2Telemetry({
  win = typeof window !== "undefined" ? window : null,
  doc = typeof document !== "undefined" ? document : null,
  record = recordP2Event,
  setContext = setUiContext,
  storage = safeLocalStorage(),
  locks = typeof navigator !== "undefined" && navigator.locks ? navigator.locks : null,
  getEntry = () => entry,
  makeTracker = createAttentionTracker,
  makeVisits = createVisits,
  onAttended = (surfaceName, forecastId) => intentEmitter().noteAttended(surfaceName, forecastId),
  buildSha = CLIENT_BUILD_SHA,
  registryVersion = REGISTRY_VERSION,
} = {}) {
  const override = win?.[SEAM_GLOBAL] ?? null;
  const now = override?.now ? () => override.now() : () => performance.now();
  const wallNow = override?.wallNow ? () => override.wallNow() : () => Date.now();
  const seam = {
    visible: override?.visible
      ? () => Boolean(override.visible())
      : () => !doc || doc.visibilityState !== "hidden",
    focused: override?.focused
      ? () => Boolean(override.focused())
      : () => !doc || typeof doc.hasFocus !== "function" || doc.hasFocus(),
  };

  let groups = { visits: false, attention: false };
  let flags = {};
  let visits = null;
  let tracker = null;
  let visitStamped = false;
  let listening = false;
  let present = false;
  let appState = null;
  let tabStarted = false;
  let lastTabStart = null;
  /** The organisation of the identity visits run under ("" for none), stamped beside the visit. */
  let identityOrg = null;
  /** Whether the running tracker started with the attention group on. */
  let trackerAttention = false;
  /** The identity visits were last applied for (`user|org`). */
  let identityKey = null;

  function onVisit(id) {
    // Spans end under the visit they belong to; reading credit starts over with the visit.
    tracker?.closeAll();
    tracker?.newVisit();
    // The registry version rides beside the visit, so every P2 row (intents and friction
    // included, which run only with visits on) records the registry it was built against.
    setContext({ visit_id: id, registry_version: id !== null ? registryVersion : null });
    visitStamped = id !== null;
    tracker?.refresh();
    maybeTabStart();
  }

  function snapshot() {
    let app = null;
    try {
      app = appState ? appState() : null;
    } catch {
      app = null;
    }
    let timezone = null;
    let locale = null;
    try {
      const o = Intl.DateTimeFormat().resolvedOptions();
      timezone = o.timeZone || null;
      locale = o.locale || null;
    } catch {
      /* unknown */
    }
    return buildTabStart({
      href: win?.location?.href ?? null,
      viewportW: win?.innerWidth,
      viewportH: win?.innerHeight,
      dpr: win?.devicePixelRatio,
      flags,
      app,
      timezone,
      locale,
      buildSha,
      registryVersion,
    });
  }

  function maybeTabStart() {
    if (tabStarted || !groups.visits || !visitStamped || !appState) return;
    tabStarted = true;
    try {
      const snap = snapshot();
      record(snap);
      // The bug-report copy carries what the ring stamps on the entry: the visit and the view.
      lastTabStart = {
        ...snap,
        visit_id: visits?.visitId ?? null,
        view: ringBuffer.context?.view ?? null,
      };
    } catch {
      /* best effort */
    }
  }

  // ── activity + presence listeners ─────────────────────────────────────────

  /** The pointer's viewport position, for the tracker to re-resolve a target that left the DOM. */
  const notePointer = (e) => {
    const p = e?.touches?.[0] ?? e;
    if (typeof p?.clientX === "number" && typeof p?.clientY === "number") {
      tracker?.pointerAt?.(p.clientX, p.clientY);
    }
  };
  const onTargetActivity = (e) => {
    notePointer(e);
    if (!present) return;
    const t = now();
    tracker?.activity(t, e.target);
    if (!tracker) visits?.activity();
  };
  const onActivity = () => {
    if (!present) return;
    tracker?.activity(now());
    if (!tracker) visits?.activity();
  };
  // The browser re-sends a pointermove at the same position when the page moves under a still
  // pointer; only a move to a new position is the user.
  let lastPointer = "";
  const onPointerMove = (e) => {
    const at = `${e.clientX},${e.clientY}`;
    if (at === lastPointer) return;
    lastPointer = at;
    onTargetActivity(e);
  };
  /** Pointer over / focus: attention may move to the target; not activity. */
  const onPointerOver = (e) => {
    notePointer(e);
    if (present) tracker?.attend(now(), e.target);
  };
  const onScroll = () => {
    tracker?.scrolled();
  };
  const onPresence = (e) => {
    // Element focus / blur reach a capturing window listener too: only the window's count.
    if (e && (e.type === "focus" || e.type === "blur") && e.target !== win && e.target !== doc) {
      return;
    }
    present = seam.visible() && seam.focused();
    tracker?.notify();
  };
  const onPageHide = () => {
    present = false;
    tracker?.closeAll();
  };

  function listen(on) {
    if (on === listening || !win || !doc) return;
    listening = on;
    const method = on ? "addEventListener" : "removeEventListener";
    const opts = { capture: true, passive: true };
    try {
      for (const type of TARGET_EVENTS) doc[method](type, onTargetActivity, opts);
      for (const type of PLAIN_EVENTS) doc[method](type, onActivity, opts);
      doc[method]("pointermove", onPointerMove, opts);
      doc[method]("pointerover", onPointerOver, opts);
      doc[method]("focusin", onPointerOver, opts);
      doc[method]("scroll", onScroll, opts);
      // Capture on the window runs before services/telemetry.js's flush on hide, so the
      // final spans make the keepalive batch.
      win[method]("visibilitychange", onPresence, true);
      win[method]("pagehide", onPageHide, true);
      // A resize can move the half-viewport line without an observer threshold firing.
      win[method]("resize", onScroll, { passive: true });
      win[method]("focus", onPresence, true);
      win[method]("blur", onPresence, true);
    } catch {
      /* best effort */
    }
    if (on) present = seam.visible() && seam.focused();
  }

  function startTracker() {
    if (tracker) return;
    trackerAttention = groups.attention;
    tracker = makeTracker({
      record,
      now,
      seam,
      root: doc?.body ?? null,
      onActivity: () => visits?.activity(),
      onAttended: (surfaceName, forecastId) => onAttended(surfaceName, forecastId),
      getContext: () => {
        try {
          return appState ? { streamingIds: appState().streamingIds ?? null } : {};
        } catch {
          return {};
        }
      },
    });
    tracker.start();
  }

  function stopTracker() {
    if (!tracker) return;
    tracker.stop();
    tracker = null;
  }

  function installTestHook() {
    if (!override || !win) return;
    win[TEST_HOOK_GLOBAL] = {
      checkpoint: () => tracker?.checkpoint(),
      notify: () => onPresence(),
      refresh: () => tracker?.refresh(),
      visitId: () => visits?.visitId ?? null,
      // The view every event is stamped with now; the replay's end check reads it once settled.
      view: () => ringBuffer.context?.view ?? null,
    };
  }

  return {
    /**
     * Stop whatever `next` turns off, while its group is still on, so its final events are
     * recorded (services/telemetry.js calls this before applying the groups).
     */
    prepare(next) {
      try {
        // Before the groups turn on (telemetry.js enable), so intents held until this answer
        // and replayed by the group switch already carry the registry version.
        if (next?.visits === true) setContext({ registry_version: registryVersion });
        // An attention transition restarts the tracker (it also runs for intents alone): spans
        // accrued while attention was off must not reach the first recorded part.
        const attentionChanges = tracker && (next?.attention === true) !== trackerAttention;
        if ((next?.attention !== true && next?.intent !== true) || attentionChanges) stopTracker();
        if (next?.visits !== true && visits) {
          visits.stop();
          visits = null;
        }
      } catch {
        /* never surface */
      }
    },
    /**
     * Apply one /api/me answer: `next` the group state (null = stream off), `identity`
     * `{user, org, flags}` (lib/telemetry/tabStart.js telemetryIdentityFromMe).
     */
    apply(next, identity = null) {
      try {
        groups = {
          visits: next?.visits === true,
          attention: next?.attention === true,
          intent: next?.intent === true,
        };
        flags = identity?.flags ?? {};
        const visitsOn = groups.visits && Boolean(identity?.user);
        // `identity_org` (the organisation the entries are captured under, "" for none) lets
        // the server refuse to attribute entries restored after an account switch to the
        // session's organisation. Stamped whenever telemetry runs signed in, P2 groups or not.
        const nextOrg = next && identity?.user ? String(identity.org ?? "") : null;
        if (nextOrg !== identityOrg) {
          // Spans still open belong to the previous identity: end them under its org first.
          if (identityOrg !== null) tracker?.closeAll();
          identityOrg = nextOrg;
          setContext({ identity_org: nextOrg });
        }
        const nextKey = visitsOn ? `${identity.user}|${identity.org ?? ""}` : null;
        const switched = Boolean(nextKey && identityKey && nextKey !== identityKey);
        if (switched) {
          // An account switch: the snapshot described the previous account, and the new
          // one's starting state is not this tab's, so there is none to record or attach.
          lastTabStart = null;
          tabStarted = true;
        }
        if (nextKey) identityKey = nextKey;
        if (visitsOn) {
          if (!visits) {
            visits = makeVisits({
              record,
              onVisit,
              entry: getEntry,
              storage,
              locks,
              win,
              wallNow,
            });
          }
        } else if (visits) {
          visits.stop();
          visits = null;
        }
        // The tracker also runs for the intent group alone: `forecast.result_seen` needs to know
        // when a result is attended. Its `attention.*` records still need the attention group
        // (lib/telemetry/groups.js recordP2Event).
        if (groups.attention || groups.intent) startTracker();
        else stopTracker();
        listen(Boolean(visits || tracker));
        installTestHook();
        // A switch since the last identity (even across a signed-out or stream-off gap, when
        // visits restarted) always starts a new visit.
        visits?.setIdentity(identity.user, identity.org ?? null, { switched });
        maybeTabStart();
      } catch {
        /* telemetry must never break the app */
      }
    },
    /** Register the app-state getter (hooks/useTelemetryAppState.js); null unregisters. */
    setAppState(getter) {
      appState = typeof getter === "function" ? getter : null;
      maybeTabStart();
    },
    /** The app's streaming runs changed: attention may change state. */
    contextChanged() {
      tracker?.contextChanged();
    },
    /**
     * The `tab.start` this page recorded, or null (bug reports attach it) — null too while the
     * visits group is off or no visit is stamped (signed out), so a report never carries it then.
     */
    tabStartSnapshot() {
      return groups.visits && visitStamped ? lastTabStart : null;
    },
    get visitId() {
      return visits?.visitId ?? null;
    },
  };
}

let singleton = null;

/** The app's P2 controller. */
export function p2Telemetry() {
  if (!singleton) singleton = createP2Telemetry();
  return singleton;
}
